import { getGraphAuthTokens } from './login-service';
import { getProfileUrl } from './urls';
import { CONSENT_STATUS, PROFILE_RULE_ID_MAP, ValidationErrorType } from './registration-service';

export interface NewsletterValidationError {
  error: ValidationErrorType;
  field: string;
  error_description?: string;
}

export interface Subscriptions {
  SubscriptionId: string;
  Subscribed?: boolean;
  UseDefault?: boolean;
}

export interface NewsletterQuestionAnswer {
  AnswerId: string;
}

export interface NewsletterQuestion {
  QuestionId: string;
  Answer: NewsletterQuestionAnswer;
}

export interface RegisterAnonymousUserPayload {
  Email: string;
  Subscriptions: Subscriptions[];
  Questions?: NewsletterQuestion[];
  CultureName?: string;
  Consent?: {
    PrivacyPolicyAcceptance: string;
    EmailConsent?: string;
    DataSharingConsent?: string;
  };
}

export interface RegisterAnonymousUserResponse {
  UnifiedId?: string;
  UserId?: string;
  error?: string;
  error_description?: string;
  Errors?: Array<{
    IsValid: boolean;
    Message: string;
    RuleId: string;
    FieldId: string;
    FieldName: string;
  }>;
  validationErrors?: NewsletterValidationError[];
}

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export interface SubscriptionStatus {
  originallySubscribed?: boolean;
  subscribed: boolean;
  useDefault: boolean;
  subscriptionId: string;
  listName: string;
  listDescription: string;
  Created?: string;
  EventDate?: string;
}

export interface UserSubscriptionsResponse {
  Subscriptions: SubscriptionStatus[];
  error?: string;
  error_description?: string;
}

async function getClientAccessToken(siteId: string): Promise<string | null> {
  try {
    const tokens = await getGraphAuthTokens({
      client_credentials: true,
      siteId,
      failTrigger: false,
    });

    if (!tokens || !tokens.access_token) {
      console.error('Failed to retrieve access token: No response or access token received');
      return null;
    }

    return tokens.access_token;
  } catch (error) {
    console.error('Error getting client access token:', error);
    return null;
  }
}

async function registerAnonymousUser(
  accessToken: string,
  failTrigger: boolean,
  payload: RegisterAnonymousUserPayload
): Promise<RegisterAnonymousUserResponse | null> {
  try {
    const userProfileUrl = getProfileUrl();
    const url = `${userProfileUrl}/users`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      console.error(`Registration failed with ${errorDetails}`);
      return {
        error: 'REGISTRATION_FAILED',
        error_description: errorDetails,
      };
    }

    const responseJson = (await response.json()) as RegisterAnonymousUserResponse;

    // Check for API errors in the response
    if (responseJson.Errors && responseJson.Errors.length > 0) {
      console.error('Registration API validation errors:', responseJson.Errors);

      // Map each RuleId to a structured ValidationError using the shared PROFILE_RULE_ID_MAP.
      // Any RuleId not in the map falls back to PROFILE_FAIL.
      const validationErrors: NewsletterValidationError[] = responseJson.Errors.map((err) => {
        const ruleId = err.RuleId?.toLowerCase() || '';
        const mapping = PROFILE_RULE_ID_MAP[ruleId];
        return mapping
          ? { error: mapping.type, field: mapping.field, error_description: err.Message }
          : {
              error: 'PROFILE_FAIL' as ValidationErrorType,
              field: 'form',
              error_description: err.Message,
            };
      });

      return {
        ...responseJson,
        error: 'VALIDATION_FAILED',
        validationErrors,
      };
    }

    return responseJson;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`Error registering anonymous user: ${errorMessage}`);

    if (failTrigger) {
      throw new Error(`Error registering anonymous user: ${errorMessage}`);
    }

    return { error: 'GENERIC_ERROR' };
  }
}

export async function signupForNewsletter(
  email: string,
  subscriptions?: Subscriptions[],
  questions?: NewsletterQuestion[],
  siteId?: string,
  cultureName?: string
): Promise<RegisterAnonymousUserResponse | null> {
  try {
    // Use environment variable if siteId not provided
    const resolvedSiteId = siteId || process.env.GMI_GRAPH_SITE_ID || '';

    // Centralized input validation
    const validation = validateSignupInputs(email, resolvedSiteId);
    if (!validation.isValid) {
      return { error: validation.error };
    }

    // Get client credentials access token
    const accessToken = await getClientAccessToken(resolvedSiteId);
    if (!accessToken) {
      console.error('Failed to obtain access token for newsletter signup');
      return { error: 'AUTH_FAILED' };
    }

    const payload: RegisterAnonymousUserPayload = {
      Email: email,
      // Ensure Subscribed is always explicitly true for each subscription so Profile
      // registers the opt-in regardless of whether the caller included the flag.
      Subscriptions: (subscriptions || []).map((sub) => ({
        ...sub,
        Subscribed: sub.Subscribed !== false, // default true; only false when explicitly false
      })),
      Questions: questions,
      ...(cultureName && { CultureName: cultureName }),
      Consent: {
        PrivacyPolicyAcceptance: CONSENT_STATUS.PRIVACY_POLICY,
        EmailConsent: CONSENT_STATUS.EMAIL,
        DataSharingConsent: CONSENT_STATUS.DATA_SHARING,
      },
    };

    // Register the user with newsletter subscriptions
    const result = await registerAnonymousUser(accessToken, false, payload);

    if (result?.error) {
      return result;
    }

    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`Newsletter signup error: ${errorMessage}`);
    return { error: 'SIGNUP_FAILED' };
  }
}

/**
 * Centralized validation for newsletter signup parameters
 */
function validateSignupInputs(email: string, siteId: string): ValidationResult {
  if (!email) {
    console.error('Email is required for newsletter signup');
    return { isValid: false, error: 'MISSING_EMAIL' };
  }

  if (!siteId) {
    console.error('Site ID is required for newsletter signup');
    return { isValid: false, error: 'MISSING_SITE_ID' };
  }

  return { isValid: true };
}

/**
 * Check if user is subscribed to a specific newsletter
 * @param userId - The user's ID
 * @param subscriptionId - The subscription ID to check
 * @param siteId - Site ID for authentication
 * @returns Promise<boolean> - true if subscribed, false otherwise
 */
export async function isUserSubscribed(
  userId: string,
  subscriptionId: string,
  accessToken?: string
): Promise<boolean> {
  try {
    // const accessToken = await getClientAccessToken(siteId);

    if (!accessToken) {
      console.error('Failed to obtain access token for subscription check');
      return false;
    }

    const userSubscriptions = await getUserSubscriptions(userId, accessToken);

    if (!userSubscriptions || userSubscriptions.error) {
      console.error('Failed to get user subscriptions:', userSubscriptions?.error);
      return false;
    }

    // Check if user is subscribed to the specific subscription
    const subscription = userSubscriptions.Subscriptions.find(
      (sub) => sub.subscriptionId === subscriptionId
    );

    return subscription?.subscribed === true;
  } catch (error) {
    console.error('Error checking user subscription:', error);
    return false;
  }
}

/**
 * Get all user subscriptions
 * @param userId - The user's ID
 * @param accessToken - Access token for API call
 * @returns Promise<UserSubscriptionsResponse | null>
 */
async function getUserSubscriptions(
  userId: string,
  accessToken: string
): Promise<UserSubscriptionsResponse | null> {
  try {
    const userProfileUrl = getProfileUrl();
    const url = `${userProfileUrl}/metadata/subscriptions/${userId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      return {
        Subscriptions: [],
        error: 'SUBSCRIPTIONS_FETCH_FAILED',
        error_description: errorDetails,
      };
    }

    const subscriptionsData = (await response.json()) as UserSubscriptionsResponse;
    return subscriptionsData;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return {
      Subscriptions: [],
      error: 'GENERIC_ERROR',
      error_description: errorMessage,
    };
  }
}

/**
 * Session cookie management for newsletter signup state
 */
export class NewsletterSessionManager {
  private static readonly COOKIE_NAME = 'newsletter_signup_completed';

  /**
   * Mark newsletter signup as completed in session
   * @param subscriptionId - The subscription ID that was completed
   */
  static markSignupCompleted(subscriptionId: string): void {
    if (typeof window === 'undefined') return; // Server-side check

    try {
      const existingData = this.getCompletedSignups();
      const updatedData = {
        ...existingData,
        [subscriptionId]: {
          completed: true,
          timestamp: new Date().toISOString(),
        },
      };

      // Store in sessionStorage (persists until tab is closed)
      sessionStorage.setItem(this.COOKIE_NAME, JSON.stringify(updatedData));
    } catch (error) {
      console.error('Error storing newsletter signup state:', error);
    }
  }

  /**
   * Check if newsletter signup was completed for a subscription
   * @param subscriptionId - The subscription ID to check
   * @returns boolean - true if signup was completed in this session
   */
  static isSignupCompleted(subscriptionId: string): boolean {
    if (typeof window === 'undefined') return false; // Server-side check

    try {
      const completedSignups = this.getCompletedSignups();
      return completedSignups[subscriptionId]?.completed === true;
    } catch (error) {
      console.error('Error reading newsletter signup state:', error);
      return false;
    }
  }

  /**
   * Get all completed signups from session
   */
  private static getCompletedSignups(): Record<string, { completed: boolean; timestamp: string }> {
    if (typeof window === 'undefined') return {}; // Server-side check

    try {
      const stored = sessionStorage.getItem(this.COOKIE_NAME);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error parsing newsletter signup state:', error);
      return {};
    }
  }

  /**
   * Remove signup completed state for a specific subscription (e.g. after unsubscribing)
   * @param subscriptionId - The subscription ID to remove
   */
  static removeSignupCompleted(subscriptionId: string): void {
    if (typeof window === 'undefined') return; // Server-side check

    try {
      const existingData = this.getCompletedSignups();
      if (!existingData[subscriptionId]) return;
      delete existingData[subscriptionId];
      if (Object.keys(existingData).length === 0) {
        sessionStorage.removeItem(this.COOKIE_NAME);
      } else {
        sessionStorage.setItem(this.COOKIE_NAME, JSON.stringify(existingData));
      }
    } catch (error) {
      console.error('Error removing newsletter signup state:', error);
    }
  }

  /**
   * Clear all newsletter signup state (for testing/debugging)
   */
  static clearSignupState(): void {
    if (typeof window === 'undefined') return; // Server-side check

    try {
      sessionStorage.removeItem(this.COOKIE_NAME);
    } catch (error) {
      console.error('Error clearing newsletter signup state:', error);
    }
  }
}
